import { Howl } from "howler";

import type { Sound } from "../types";
interface AudioPlayerCallbacks {
  onEnd?: () => void;
  onLoad?: (duration: number) => void;
  onPlay?: () => void;
  onProgress?: (currentTime: number) => void;
  onStop?: () => void;
}

enum AudioPlayerState {
  LOADED = 0,
  LOADING = 1,
  PAUSED = 2,
  PLAYING = 3,
}

export class AudioPlayerInstance {
  private animationFrameId: null | number = null;
  private callbacks: AudioPlayerCallbacks;
  private howl: Howl;
  private onplay: () => void;
  private onstop: () => void;
  private state = AudioPlayerState.LOADING;
  constructor(
    url: string,
    callbacks: AudioPlayerCallbacks = {},
    loop: boolean = false,
    public sound?: Sound,
  ) {
    this.callbacks = callbacks;
    this.onplay = () => {
      this.callbacks.onPlay?.();
      this.cancelAnimationFrame(); // in case this gets called multiple times
      // Set up animation frame loop
      const updateProgress = () => {
        if (this.howl.playing()) {
          this.callbacks.onProgress?.(this.howl.seek());
          this.animationFrameId = requestAnimationFrame(updateProgress);
        }
      };
      this.animationFrameId = requestAnimationFrame(updateProgress);
    };

    this.onstop = () => {
      this.state = AudioPlayerState.LOADED;
      this.cancelAnimationFrame();
      this.callbacks.onStop?.();
    };

    this.howl = new Howl({
      src: [url],
      html5: false, // HTML5 does not support gapless audio playback
      preload: true, // Preload the sound
      loop,
      onload: () => {
        this.state = AudioPlayerState.LOADED;
        this.callbacks.onLoad?.(this.howl.duration());
      },
      onplay: this.onplay,
      onend: () => {
        this.cancelAnimationFrame();
        if (!this.howl.loop()) {
          this.callbacks.onEnd?.();
          this.state = AudioPlayerState.LOADED;
        }
      },
      onstop: this.onstop,
    });
  }

  private cancelAnimationFrame() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  duration() {
    return this.howl.duration();
  }

  loop(loop: boolean) {
    this.howl.loop(loop);
  }

  pause() {
    if (this.state === AudioPlayerState.PLAYING) {
      this.howl.pause();
      this.state = AudioPlayerState.PAUSED;
    }
  }

  paused() {
    return this.state === AudioPlayerState.PAUSED;
  }

  play() {
    this.howl.seek(0);
    this.callbacks.onProgress?.(0);
    this.state = AudioPlayerState.PLAYING;
    this.howl.play();
  }

  playing() {
    return this.state === AudioPlayerState.PLAYING;
  }

  prepareForPlay() {
    this.howl.seek(0);
    this.callbacks.onProgress?.(0);
    this.state = AudioPlayerState.PAUSED;
    this.onplay();
  }

  resume() {
    if (this.state === AudioPlayerState.PAUSED) {
      this.howl.play();
      this.state = AudioPlayerState.PLAYING;
    }
  }

  seek(position: number) {
    this.howl.seek(position);
  }

  stop() {
    this.howl.stop();
    this.onstop(); // we call it directly otherwise the audio will take a few ms to be removed from play head
  }

  unload() {
    this.cancelAnimationFrame();
    this.howl.unload();
  }
}
